import { TransitOperationLog } from './types'

const PRE_LODGED_PRESENTATION_NOTIFICATION = 'IE170'

const AMENDMENT_ACCEPTANCE = 'IE004'

const ASSESSMENT_COMPLETE = 'IE3N03'

/** ICS2 ENS Amendment notification — not to be confused with the transit amendment acceptance above. */
const AMENDMENT_NOTIFICATION = 'IE3N10'

/**
 * Messages customs answers a presentation notification (IE170) with: an MRN
 * allocation (IE028), a control decision (IE060), a business rejection (IE056)
 * or a functional error (IE906, IE917). A country may answer with more; those
 * come from its PreLodgedDeclarationConfig.extraClosingSuffixes.
 *
 * A refusal ends the wait just as an acceptance does — customs has answered, so
 * nothing is in flight any more. It does not follow that the consignment may be
 * presented again; see isPreLodgedPresentationNotificationAwaitingCorrection.
 */
const PRE_LODGED_PRESENTATION_NOTIFICATION_RESPONSES = ['IE028', 'IE056', 'IE060', 'IE906', 'IE917']

/**
 * Functional errors, as opposed to a decision on the presentation itself. PL
 * refused a presentation with IE906 / R0005 because the estimated arrival dates
 * at the declared transit offices had passed.
 */
const PRE_LODGED_PRESENTATION_NOTIFICATION_FUNCTIONAL_ERRORS = ['IE906', 'IE917']

/** A Serbian message name; its IE code is the middle part (RS028C is IE028, RSA29D is IEA29). */
const SERBIAN_MESSAGE = /^RS(\d{3}|A\d{2})[CD]$/

const serbianIeCodeOf = (messageContext: string): string | null => {
  const serbianMessage = SERBIAN_MESSAGE.exec(messageContext)

  return serbianMessage ? `IE${serbianMessage[1]}` : null
}

/**
 * Serbian messages the declarant sends. The server labels them with their IE code
 * until they are signed and sent, and the sent row with its Serbian name.
 */
const SERBIAN_OUTBOUND_MESSAGES = ['RSA15D', 'RS015C', 'RSA13D', 'RS013C', 'RS014C', 'RS170C']

/** The unsigned drafts logged before the messages above are signed and sent. */
const SERBIAN_UNSIGNED_DRAFTS = SERBIAN_OUTBOUND_MESSAGES.map((message) => serbianIeCodeOf(message) ?? message)

/**
 * The code a row is read by: the IE code of a Serbian message (RS028C is IE028,
 * the sent RS170C is IE170), otherwise the label itself. An unsigned Serbian draft
 * has no code, so the rules and the status icon both read the row that went out.
 */
export const messageCodeOf = (messageContext: string, country: string | null | undefined): string | null => {
  if (country === 'RS' && SERBIAN_UNSIGNED_DRAFTS.includes(messageContext)) {
    return null
  }

  return serbianIeCodeOf(messageContext) ?? messageContext
}

export type MessageLogEntry = {
  code: string
  created: Date
}

export const readMessageLog = (
  eventLogs: TransitOperationLog[] | undefined,
  country: string | null | undefined,
): MessageLogEntry[] => (eventLogs ?? []).flatMap(({ messageContext, created }) => {
  const code = messageCodeOf(messageContext, country)

  return code == null ? [] : [{ code, created }]
})

const hasAnyMessageCode = (codes: string[]) => (entry: MessageLogEntry): boolean => codes.some((suffix) => entry.code.endsWith(suffix))

export const findByMessageCode = (
  messageLog: MessageLogEntry[],
  ...codes: string[]
): MessageLogEntry[] => messageLog.filter(hasAnyMessageCode(codes))

export const hasMessageCode = (
  messageLog: MessageLogEntry[],
  ...codes: string[]
): boolean => messageLog.some(hasAnyMessageCode(codes))

export const countByMessageCode = (
  messageLog: MessageLogEntry[],
  ...codes: string[]
): number => findByMessageCode(messageLog, ...codes).length

const latestCreatedAt = (
  messageLog: MessageLogEntry[],
  suffixes: string[],
): number | null => {
  const timestamps = findByMessageCode(messageLog, ...suffixes)
    .filter((entry) => entry.created)
    .map((entry) => new Date(entry.created).getTime())

  return timestamps.length > 0 ? Math.max(...timestamps) : null
}

/**
 * True while a submission message has been sent and no closing message has
 * arrived after it — the latest submission is still being processed. Entries
 * without a created timestamp are ignored, and a closing message created at
 * the same instant as the submission counts as closing.
 */
export const isSubmissionAwaitingAnswer = (
  messageLog: MessageLogEntry[],
  submissionSuffix: string,
  closingSuffixes: string[],
): boolean => {
  const sentAt = latestCreatedAt(messageLog, [submissionSuffix])

  if (sentAt === null) {
    return false
  }

  const answeredAt = latestCreatedAt(messageLog, closingSuffixes)

  return answeredAt === null || answeredAt < sentAt
}

/**
 * True while a presentation notification has been sent and customs has not
 * answered it yet — sending another one now would reach customs before the
 * first is processed. The message log is the only record of it: the client
 * sends the presentation through the server, which logs the IE170.
 */
export const isPreLodgedPresentationNotificationAwaitingAnswer = (
  messageLog: MessageLogEntry[],
  extraClosingSuffixes: string[] = [],
): boolean => isSubmissionAwaitingAnswer(
  messageLog,
  PRE_LODGED_PRESENTATION_NOTIFICATION,
  [...PRE_LODGED_PRESENTATION_NOTIFICATION_RESPONSES, ...extraClosingSuffixes],
)

/**
 * True when the last presentation was refused with a functional error and the
 * declaration has not been amended since. The movement is waiting for a
 * correction, and only an amendment delivers one: presenting again from here is
 * answered with an out-of-sequence error, because the refusal did not put the
 * declaration back into a state that accepts IE170. An accepted amendment
 * (IE004) does, which is why the recorded recovery reads
 * IE170 -> IE906 -> IE013 -> IE004 -> IE170.
 *
 * Scoped to functional errors on purpose. IE056 also refuses a presentation,
 * but it is a business rejection and it is unconfirmed whether it needs the
 * same amendment before re-presenting, so it is left alone.
 */
export const isPreLodgedPresentationNotificationAwaitingCorrection = (
  messageLog: MessageLogEntry[],
): boolean => {
  const sentAt = latestCreatedAt(messageLog, [PRE_LODGED_PRESENTATION_NOTIFICATION])

  if (sentAt === null) {
    return false
  }

  const refusedAt = latestCreatedAt(messageLog, PRE_LODGED_PRESENTATION_NOTIFICATION_FUNCTIONAL_ERRORS)

  // An error older than the presentation belongs to an earlier exchange, and one
  // older than the newest presentation was already corrected and re-presented.
  if (refusedAt === null || refusedAt < sentAt) {
    return false
  }

  const amendedAt = latestCreatedAt(messageLog, [AMENDMENT_ACCEPTANCE])

  return amendedAt === null || amendedAt < refusedAt
}

/**
 * True when ICS2 has completed the risk assessment of the current ENS version:
 * an Assessment Complete (IE3N03) exists and no Amendment notification (IE3N10)
 * arrived after the latest one.
 */
export const isEnsAssessmentComplete = (
  messageLog: MessageLogEntry[],
): boolean => {
  const assessedAt = latestCreatedAt(messageLog, [ASSESSMENT_COMPLETE])

  if (assessedAt === null) {
    return false
  }

  const amendedAt = latestCreatedAt(messageLog, [AMENDMENT_NOTIFICATION])

  return amendedAt === null || amendedAt <= assessedAt
}

export type EventLogStatus = 'ACCEPTED' | 'REFUSED' | ''

const ACCEPTED_MESSAGE_SUFFIXES = ['IE004', 'IE014', 'IE028', 'IE928', 'IEA29']

// 'CEP rejection' is SK's Neprijaté: CEP refused the signed submission before it reached NCTS.
const REFUSED_MESSAGE_SUFFIXES = ['IE016', 'IE056', 'IE906', 'IE917', 'IEA91', 'CEP rejection']

export const resolveEventLogStatus = (messageContext: string | undefined): EventLogStatus => {
  const context = messageContext ?? ''
  const endsWithAny = (suffixes: string[]) => suffixes.some((suffix) => context.endsWith(suffix))

  if (endsWithAny(ACCEPTED_MESSAGE_SUFFIXES)) {
    return 'ACCEPTED'
  }
  if (endsWithAny(REFUSED_MESSAGE_SUFFIXES)) {
    return 'REFUSED'
  }
  return ''
}
